import { createContext, useContext } from "react"
import type { EditStageContextProps } from "src/context/editStageContext/EditStageContext"
import type { EditStageContextPropsWithEditMode } from "src/context/editStageContext/useEditStageContext"
import { useEditStageContext, useEditStageContextForStage } from "src/context/editStageContext/useEditStageContext"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import type { Event, NotifyMessage, PayloadType } from "./VehicleDetailsContextProvider"

/*
 values that should be available in all vehicle add stages
 */
export interface VehicleDetailsContextProps {
	inventoryId: string
	notify: <T extends Event>(message: NotifyMessage<T>) => void
	subscribe: <T extends Event>(event: T, callbackFn: (payload: PayloadType<T>) => void) => void
}

export const VehicleDetailsContext = createContext<VehicleDetailsContextProps | null>(null)

export const useVehicleDetailsContext: () => VehicleDetailsContextProps & EditStageContextProps<VehicleDetailsStage> =
	() => {
		const vehicleDetailsContext = useContext(VehicleDetailsContext)
		if (vehicleDetailsContext == null) {
			throw new Error("VehicleDetailsContext is required for this component")
		}
		const editStageContext = useEditStageContext<VehicleDetailsStage>()
		return {
			...vehicleDetailsContext,
			...editStageContext,
		}
	}

export const useVehicleDetailsContextForStage = (
	stageId: VehicleDetailsStage,
): VehicleDetailsContextProps & EditStageContextPropsWithEditMode<VehicleDetailsStage> => {
	const vehicleDetailsContext = useContext(VehicleDetailsContext)
	if (vehicleDetailsContext == null) {
		throw new Error("VehicleDetailsContext is required for this component")
	}
	const editStageContext = useEditStageContextForStage<VehicleDetailsStage>(stageId)
	return {
		...vehicleDetailsContext,
		...editStageContext,
	}
}
